import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson, apiSend } from "@/api/client";
import { invoiceKeys } from "@/api/invoices";
import { segment, withQuery } from "@/api/query";

/*
 * Mirrors api/src/pbo_invoicing/payments/schemas.py. Money is a string ("1400.00").
 * `amount` settles the invoice and includes the bank charges deducted on the way.
 */

export type PaymentDetailsInput = {
  /** Which money account it landed in; may be set later. */
  received_into_id: string | null;
  inr_credited: string | null;
  fx_rate: string | null;
  reference: string;
  fira_ref: string;
  purpose_code: string;
  notes: string;
};

/** How the money reached us. Cash is its own method, not a note in the margin. */
export type PaymentMethod = "bank_transfer" | "cash" | "cheque" | "other";

export const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: "bank_transfer", label: "Bank transfer (SWIFT)" },
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];

export function methodLabel(method: string): string {
  return PAYMENT_METHODS.find((m) => m.value === method)?.label ?? method;
}

export type PaymentInput = PaymentDetailsInput & {
  received_on: string;
  amount: string;
  bank_charges: string;
  method: PaymentMethod;
  /* What was physically received, when that is not the invoice currency: SAR 5,250 cash
     against a USD 1,400 invoice, at SAR 3.75 to the dollar. */
  received_currency: string;
  received_amount: string | null;
  received_rate: string | null;
  received_by: string;
  received_place: string;
};

export type Payment = Omit<PaymentInput, "received_currency"> & {
  id: string;
  invoice_id: string;
  currency: string;
  received_currency: string | null;
  net_amount: string;
  voided_at: string | null;
  voided_by_username: string | null;
  void_reason: string | null;
  /** The receipt that acknowledges this payment, once one has been issued. */
  receipt_id: string | null;
  receipt_no: string | null;
  created_by_username: string | null;
  version: number;
  created_at: string;
  updated_at: string;
};

export type PaymentRegisterRow = Payment & {
  invoice_no: string | null;
  client_code: string;
  vessel_name: string | null;
};

export type PaymentFilters = {
  receivedFrom?: string;
  receivedTo?: string;
  includeVoided?: boolean;
  /** Only live foreign payments with neither the INR credited nor a rate. */
  withoutRupees?: boolean;
  clientId?: string;
  limit?: number;
};

export const paymentKeys = {
  all: ["payments"] as const,
  register: (filters: PaymentFilters) => ["payments", "register", filters] as const,
};

export function pickPaymentDetails(input: PaymentDetailsInput): PaymentDetailsInput {
  const { received_into_id, inr_credited, fx_rate, reference, fira_ref, purpose_code, notes } =
    input;
  return { received_into_id, inr_credited, fx_rate, reference, fira_ref, purpose_code, notes };
}

// --------------------------------------------------------------------------- requests

export function recordPayment(invoiceId: string, input: PaymentInput): Promise<Payment> {
  return apiSend<Payment>(`/invoices/${segment(invoiceId)}/payments`, "POST", input);
}

export function updatePaymentDetails(
  id: string,
  input: PaymentDetailsInput,
  version: number,
): Promise<Payment> {
  return apiSend<Payment>(`/payments/${segment(id)}`, "PUT", pickPaymentDetails(input), version);
}

export function voidPayment(id: string, reason: string, version: number): Promise<Payment> {
  return apiSend<Payment>(`/payments/${segment(id)}/void`, "POST", { reason }, version);
}

export function listPayments(filters: PaymentFilters = {}): Promise<PaymentRegisterRow[]> {
  return apiJson<PaymentRegisterRow[]>(
    withQuery("/payments", {
      received_from: filters.receivedFrom,
      received_to: filters.receivedTo,
      include_voided: filters.includeVoided,
      without_rupees: filters.withoutRupees,
      client_id: filters.clientId,
      limit: filters.limit,
    }),
  );
}

// --------------------------------------------------------------------------- hooks

export function usePaymentRegister(filters: PaymentFilters) {
  return useQuery({ queryKey: paymentKeys.register(filters), queryFn: () => listPayments(filters) });
}

function usePaymentMutation<Variables>(
  invoiceId: string,
  mutationFn: (variables: Variables) => Promise<Payment>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) }),
        queryClient.invalidateQueries({ queryKey: invoiceKeys.lists }),
        queryClient.invalidateQueries({ queryKey: paymentKeys.all }),
      ]),
  });
}

export function useRecordPayment(invoiceId: string) {
  return usePaymentMutation(invoiceId, (input: PaymentInput) => recordPayment(invoiceId, input));
}

export function useUpdatePaymentDetails(invoiceId: string) {
  return usePaymentMutation(
    invoiceId,
    ({ id, input, version }: { id: string; input: PaymentDetailsInput; version: number }) =>
      updatePaymentDetails(id, input, version),
  );
}

export function useVoidPayment(invoiceId: string) {
  return usePaymentMutation(
    invoiceId,
    ({ id, reason, version }: { id: string; reason: string; version: number }) =>
      voidPayment(id, reason, version),
  );
}
